import type {
  ApiRequest,
  ApiResponse,
  SaveRequest,
  ReviewData,
  CriterionSaveData,
  CriterionData,
  ErrorResponse,
  LoginRequest,
  RegisterRequest,
  User,
  DashboardResponse,
  ExaminationSummary,
  ApiExtractResponse,
  ApiCriterionData,
  ExaminationLoadResponse,
  CopyExaminationResponse,
  RubricGenerationRequest,
  RubricGenerationResponse,
  RubricDirectGenerationRequest,
  RubricCell
} from '../types';
import { setUserInMemory, getUserFromMemory } from '../utils/auth';

// ユーザー情報を保存する関数（後方互換性のため残す）
export const setUser = (user: User): void => {
  // メモリに保存
  setUserInMemory(user);
  // 後方互換性のため、localStorageにも保存（段階的に削除予定）
  localStorage.setItem('user', JSON.stringify(user));
};

// ユーザー情報を取得する関数
export const getUser = (): User | null => {
  // まずメモリから取得を試みる
  const memoryUser = getUserFromMemory();
  if (memoryUser) {
    return memoryUser;
  }
  
  // 後方互換性のため、localStorageからも取得
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;
  if (user) {
    setUserInMemory(user);
  }
  return user;
};

// 認証状態をチェックする関数（セッションベース）
export const isAuthenticated = (): boolean => {
  // メモリまたはlocalStorageにユーザー情報があるかをチェック
  return !!getUser();
};

// API設定 - 環境に応じて自動調整
const getApiBaseUrl = (): string => {
  // 環境変数が明示的に設定されている場合はそれを使用（ただしlocalhostは無視）
  if (import.meta.env.VITE_API_URL && !import.meta.env.VITE_API_URL.includes('localhost')) {
    return import.meta.env.VITE_API_URL;
  }
  
  // 現在のホストに基づいて決定
  const currentHost = window.location.host;
  const protocol = window.location.protocol;
  
  if (currentHost === 'evaloid.com') {
    // 本番環境：同じホスト、同じプロトコル、ポート番号なし
    return `${protocol}//${currentHost}`;
  } else if (currentHost.includes('localhost') || currentHost.includes('127.0.0.1')) {
    // ローカル開発環境
    return 'http://localhost:8080';
  } else {
    // その他の環境：HTTPSを使用、ポート番号なし
    return `https://${currentHost}`;
  }
};

const API_CONFIG = {
  baseUrl: getApiBaseUrl(),
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
  headers: {
    'Content-Type': 'application/json',
  }
};

// デバッグ用：API URLをコンソールに出力
console.log('API Base URL:', API_CONFIG.baseUrl);
console.log('Current host:', window.location.host);
console.log('VITE_API_URL env:', import.meta.env.VITE_API_URL);
console.log('All env vars:', import.meta.env);

// APIエラークラス
export class ApiError extends Error {
  public status?: number;
  public details?: string;
  
  constructor(
    message: string,
    status?: number,
    details?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// APIクライアントクラス
class EvaloidApiClient {
  private userId: string;

  constructor() {
    // 認証されたユーザーのIDを使用、なければ仮のIDを生成
    const user = getUser();
    this.userId = user?.id || this.generateTempUserId();
  }

  private generateTempUserId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 統一APIリクエストメソッド
  private async makeRequest<T>(
    messageType: string,
    content: unknown,
    timeout: boolean = false
  ): Promise<T> {
    const request: ApiRequest = {
      userId: this.userId,
      role: "user",
      messageType,
      timeout,
      content
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);      // Cookie認証のため、credentialsを含める
      const response = await fetch(`${API_CONFIG.baseUrl}/api`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: JSON.stringify(request),
        signal: controller.signal,
        credentials: 'include' // セッションCookieを含める
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        if (response.status === 401) {
          // 401エラーはfetchInterceptorで処理されるが、ApiErrorも投げる
          throw new ApiError(
            'Unauthorized',
            401,
            'Session expired or invalid'
          );
        }
        throw new ApiError(
          `HTTP Error: ${response.status}`,
          response.status,
          `Request failed with status ${response.status}`
        );
      }

      const result: ApiResponse = await response.json();
      
      // エラーレスポンスの処理
      if (result.messageType.includes('error')) {
        const errorContent = result.content as ErrorResponse;
        throw new ApiError(
          errorContent.error,
          undefined,
          errorContent.details
        );
      }

      return result.content;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new ApiError('Request timeout', 408, 'The request took too long to complete');
        }
        throw new ApiError(
          'Network error',
          undefined,
          error.message
        );
      }
      
      throw new ApiError('Unknown error occurred');
    }
  }

  // AI観点抽出
  async extractCriteria(descriptions: string[], products?: ReviewData[]): Promise<CriterionData[]> {
    try {
      // 現在の言語設定を取得
      const currentLanguage = localStorage.getItem('i18nextLng') || 'ja';
      
      // プロンプトを改善するための追加指示
      const promptInstructions = currentLanguage === 'ja' 
        ? '具体的で実用的な評価観点を抽出してください。階層名や番号付きの見出しは含めないでください。各観点は独立した評価項目として提示してください。'
        : 'Extract specific and practical evaluation criteria. Do not include hierarchical names or numbered headings. Present each criterion as an independent evaluation item.';
      
      const requestPayload = { 
        description: descriptions,
        products: products || [],
        language: currentLanguage, // 言語コードを送信
        instructions: promptInstructions // 追加指示
      };
      
      const response = await this.makeRequest<ApiExtractResponse | ApiCriterionData[]>(
        'extract',
        requestPayload
      );
      
      // レスポンスの形式を検証
      console.log('API Response:', response);
      
      let rawCriteria: CriterionData[] = [];
      
      // レスポンスが配列として直接返される場合
      if (Array.isArray(response)) {
        rawCriteria = (response as ApiCriterionData[]).map((criterion) => ({
          title: criterion.criterionTitle || '',
          description: Array.isArray(criterion.criterionDescription) 
            ? criterion.criterionDescription 
            : []
        }));
      }
      // レスポンスがオブジェクトで criteria プロパティを持つ場合
      else if (response && response.criteria && Array.isArray(response.criteria)) {
        rawCriteria = (response as ApiExtractResponse).criteria.map((criterion) => ({
          title: criterion.criterionTitle || '',
          description: Array.isArray(criterion.criterionDescription) 
            ? criterion.criterionDescription 
            : []
        }));
      }
      
      // 不適切な観点をフィルタリング
      const filteredCriteria = rawCriteria.filter(criterion => {
        const title = criterion.title.trim();
        
        // 空のタイトルは除外
        if (!title) return false;
        
        // 階層名っぽいものを除外（「リスト」「について」などを含む）
        const hierarchicalPatterns = [
          'リスト',
          'について',
          '# ',
          '## ',
          '### '
        ];
        if (hierarchicalPatterns.some(pattern => title.includes(pattern))) {
          console.log('Filtered out hierarchical criterion:', title);
          return false;
        }
        
        // 番号付きの観点から番号を除去
        const numberPattern = /^[\d０-９]+[.．]\s*/;
        if (numberPattern.test(title)) {
          criterion.title = title.replace(numberPattern, '').trim();
        }
        
        return true;
      });
      
      // レスポンスが期待する形式でない場合
      if (filteredCriteria.length === 0) {
        console.warn('No valid criteria found in response:', response);
      }
      
      return filteredCriteria;
      
    } catch (error) {
      console.error('Error in extractCriteria:', error);
      throw error;
    }
  }

  // データ保存（ID管理ベース）
  async saveData(
    review: ReviewData[],
    criteria: CriterionSaveData[],
    title?: string,
    examinationId?: string
  ): Promise<{ isSuccess: boolean; message?: string }> {
    const saveRequest: SaveRequest = { 
      saveReview: review, 
      saveCriteria: criteria, 
      saveTitle: title,
      saveExaminationId: examinationId  // 評価プロジェクトIDを追加
    };
    return await this.makeRequest('save', saveRequest);
  }

  // データ読み込み
  async loadData(): Promise<SaveRequest> {
    return await this.makeRequest('load', null);
  }

  // ダッシュボードデータ取得
  async getDashboard(): Promise<DashboardResponse> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/dashboard`, {
      headers: API_CONFIG.headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ApiError(
        `Failed to fetch dashboard: ${response.status}`,
        response.status
      );
    }

    return await response.json();
  }

  // 新しい評価プロジェクト作成
  async createExamination(title?: string, products: ReviewData[] = []): Promise<ExaminationSummary> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/examinations`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      body: JSON.stringify({
        createExamTitle: title,
        createExamProducts: products
      }),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ApiError(
        `Failed to create examination: ${response.status}`,
        response.status
      );
    }

    return await response.json();
  }

  // 評価プロジェクト読み込み（統一メソッド）
  async loadExamination(examinationId: string): Promise<ExaminationLoadResponse> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/examinations/${examinationId}`, {
      headers: API_CONFIG.headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ApiError(
        `Failed to load examination: ${response.status}`,
        response.status
      );
    }

    // バックエンドのレスポンスをそのまま返す
    return await response.json();
  }

  // 評価プロジェクト削除
  async deleteExamination(examinationId: string): Promise<void> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/examinations/${examinationId}`, {
      method: 'DELETE',
      headers: API_CONFIG.headers,
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ApiError(
        `Failed to delete examination: ${response.status}`,
        response.status
      );
    }
  }

  // 新しい評価プロジェクト作成（簡易版）
  async createNewExamination(title?: string): Promise<string> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/examinations`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      body: JSON.stringify({
        createExamTitle: title,
        createExamProducts: []
      }),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ApiError(
        `Failed to create new examination: ${response.status}`,
        response.status
      );
    }

    const result = await response.json();
    return result.examId;  }

  // ユーザーIDの取得
  getUserId(): string {
    // 最新の認証されたユーザーIDを取得
    const user = getUser();
    if (user?.id && user.id !== this.userId) {
      this.userId = user.id;
    }
    return this.userId;
  }

  // ユーザーIDの設定（認証後の更新用）
  setUserId(userId: string): void {
    this.userId = userId;
  }

  // 認証されたユーザーIDに更新
  updateToAuthenticatedUser(): void {
    const user = getUser();
    if (user?.id) {
      this.userId = user.id;
    }
  }

  // プロジェクトデータ取得（マージ機能用）
  async getProjectData(projectId: string): Promise<{ products: ReviewData[], criteria: CriterionData[] }> {
    const data = await this.loadProjectForStore(projectId);
    return {
      products: (data.reviews || []).map((p: ReviewData) => ({
        reviewTitle: p.reviewTitle,
        reviewComment: p.reviewComment || '',
        reviewLink: p.reviewLink
      })),
      criteria: (data.criteriaData || []).map((c: any) => ({
        title: c.saveCriterion,
        description: Array.isArray(c.saveDescription) ? c.saveDescription : [c.saveDescription || '']
      }))
    };
  }

  // プロジェクト作成（マージ機能用）
  async createProject(projectData: { title: string, products: ReviewData[], criteria: CriterionData[] }): Promise<string> {
    return await this.createNewExamination(projectData.title);
  }

  // データ削除
  async deleteData(): Promise<{ isSuccess: boolean; message?: string }> {
    return await this.makeRequest('delete', {});
  }

  // ルーブリック生成（差異分析ベース）
  async generateRubric(request: RubricGenerationRequest): Promise<RubricGenerationResponse> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/rubric/generate`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Rubric generation failed' }));
      throw new ApiError(`Rubric generation error: ${error.message}`, response.status, error.message);
    }

    return response.json();
  }

  // 直接ルーブリック生成（観点と評価対象から）
  async generateRubricDirect(request: RubricDirectGenerationRequest): Promise<RubricGenerationResponse> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/rubric/generate-direct`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Direct rubric generation failed' }));
      throw new ApiError(`Direct rubric generation error: ${error.message}`, response.status, error.message);
    }

    return response.json();
  }

  // ルーブリック保存
  async saveRubric(examinationId: string, rubricData: RubricCell[][], analysisData?: any[]): Promise<{ success: boolean; message?: string }> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/examinations/${examinationId}/rubric`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      credentials: 'include',
      body: JSON.stringify({
        rubric: rubricData,
        analysis: analysisData || []
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Rubric save failed' }));
      throw new ApiError(`Rubric save error: ${error.message}`, response.status, error.message);
    }

    return response.json();
  }

  // 評価コメント生成
  async generateEvaluationComment(request: {
    examinationId: string;
    productTitle: string;
    rubricDescriptions: Array<{ criterionTitle: string; description: string; grade: string }>;
    currentComment?: string;
  }): Promise<{ comment: string }> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/evaluation/generate-comment`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      credentials: 'include',
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Comment generation failed' }));
      throw new ApiError(`Comment generation error: ${error.message}`, response.status, error.message);
    }

    return response.json();
  }

  // ルーブリック読み込み
  async loadRubric(examinationId: string): Promise<any> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/examinations/${examinationId}/rubric`, {
      method: 'GET',
      headers: API_CONFIG.headers,
      credentials: 'include'
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'ルーブリック読み込みに失敗しました' }));
      throw new ApiError(`ルーブリック読み込みエラー: ${error.message}`, response.status, error.message);
    }

    return response.json();
  }

  // 評価プロジェクトコピー
  async copyExamination(
    examinationId: string,
    options: {
      title?: string;
      copyProducts: boolean;
      copyCriteria: boolean;
      copyGrades: boolean;
      copyEvaluations: boolean;
    }
  ): Promise<CopyExaminationResponse> {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/examinations/${examinationId}/copy`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      body: JSON.stringify({
        copyTitle: options.title,
        copyProducts: options.copyProducts,
        copyCriteria: options.copyCriteria,
        copyGrades: options.copyGrades,
        copyEvaluations: options.copyEvaluations
      }),
      credentials: 'include'
    });

    if (!response.ok) {
      throw new ApiError(
        `Failed to copy examination: ${response.status}`,
        response.status
      );
    }

    return await response.json();
  }

  // プロジェクトデータをストア用に変換
  async loadProjectForStore(examinationId: string): Promise<{
    saveTitle: string;
    reviews: ReviewData[];
    criteriaData: CriterionSaveData[];
    evaluationPositions: Array<{
      productIndex: number;
      criterionIndex: number;
      x: number;
      y: number;
    }>;
  }> {
    const data = await this.loadExamination(examinationId);
    
    // バックエンドの形式をストアが期待する形式に変換
    return {
      // タイトル
      saveTitle: data.examTitle || '',
      
      // 製品データ
      reviews: data.examProducts?.map((p: any) => ({
        reviewId: p.productId,
        reviewTitle: p.productTitle,
        reviewComment: p.productComment || '',
        reviewLink: p.productLink || ''
      })) || [],
      
      // 観点データ
      criteriaData: data.examCriteria?.map((c: any) => ({
        saveCriterion: c.criterionResponseTitle,
        saveDescription: c.criterionResponseDescription || [],
        saveXList: [],
        saveYList: [],
        // 等級データ
        saveGrade: c.criterionGrades?.map((g: any) => ({
          gradeLetter: g.gradeResponseLetter,
          gradeXStart: g.gradeResponseXStart
        })) || []
      })) || [],
      
      // 評価位置データ
      evaluationPositions: data.examCriteria?.flatMap((c: any, criterionIndex: number) => 
        c.criterionPositions?.map((p: any) => {
          const productIndex = data.examProducts?.findIndex((prod: any) => 
            prod.productId === p.positionProductId
          );
          if (productIndex === -1 || productIndex === undefined) return null;
          
          return {
            productIndex,
            criterionIndex,
            x: p.positionX,
            y: p.positionY
          };
        }).filter((p: any) => p !== null && p.x >= 0) || []
      ) || []
    };
  }

  // GET リクエスト用のヘルパーメソッド
  async get<T = unknown>(path: string): Promise<T> {
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}${path}`, {
        method: 'GET',
        headers: API_CONFIG.headers,
        credentials: 'include' // セッションCookieを含める
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP Error: ${response.status}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch (_e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      if (error instanceof Error) {
        throw new ApiError(
          'Network error',
          undefined,
          error.message
        );
      }
      
      throw new ApiError('Unknown error occurred');
    }
  }

  // POST リクエスト用のヘルパーメソッド
  async post<T = unknown>(path: string, data?: unknown): Promise<T> {
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}${path}`, {
        method: 'POST',
        headers: API_CONFIG.headers,
        body: data ? JSON.stringify(data) : undefined,
        credentials: 'include' // セッションCookieを含める
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP Error: ${response.status}`;
        
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch (_e) {
          errorMessage = errorText || errorMessage;
        }
        
        throw new ApiError(errorMessage, response.status);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      if (error instanceof Error) {
        throw new ApiError(
          'Network error',
          undefined,
          error.message
        );
      }
      
      throw new ApiError('Unknown error occurred');
    }
  }
}

// シングルトンインスタンス
export const apiClient = new EvaloidApiClient();

// 認証後にAPIクライアントのユーザーIDを更新する関数
export const updateApiClientUserId = (): void => {
  apiClient.updateToAuthenticatedUser();
};

// ヘルパー関数
export const withErrorHandling = async <T>(
  apiCall: () => Promise<T>,
  errorCallback?: (error: ApiError) => void
): Promise<T | null> => {
  try {
    return await apiCall();
  } catch (error) {
    if (error instanceof ApiError) {
      console.error('API Error:', error.message, error.details);
      errorCallback?.(error);
    } else {
      console.error('Unexpected error:', error);
    }
    return null;
  }
};

// デバウンス付きAPI呼び出し
export const createDebouncedApiCall = <T extends unknown[], R>(
  apiCall: (...args: T) => Promise<R>,
  delay: number = 1000
) => {
  let timeoutId: number;
  
  return (...args: T): Promise<R> => {
    return new Promise((resolve, reject) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          const result = await apiCall(...args);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, delay);
    });
  };
};

// 認証関連のAPI関数を修正
export const authAPI = {
  // ログイン
  login: async (credentials: LoginRequest): Promise<{ userId: number; user: User }> => {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/auth/login`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      body: JSON.stringify(credentials),
      credentials: 'include' // セッションCookieを受け取る
    });    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Login failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    
    // ユーザー情報をローカルストレージに保存
    if (result.user) {
      setUser(result.user);
      // APIクライアントのユーザーIDを更新
      apiClient.updateToAuthenticatedUser();
    }
    
    return result;
  },

  // ユーザー登録
  register: async (userData: RegisterRequest): Promise<{ userId: number; user: User }> => {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/auth/register`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      body: JSON.stringify(userData),
      credentials: 'include' // セッションCookieを受け取る
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Registration failed: ${response.status} - ${errorText}`);
    }    const result = await response.json();
    
    // ユーザー情報をローカルストレージに保存
    if (result.user) {
      setUser(result.user);
      // APIクライアントのユーザーIDを更新
      apiClient.updateToAuthenticatedUser();
    }
    
    return result;
  },

  // ログアウト
  logout: async (): Promise<void> => {
    await fetch(`${API_CONFIG.baseUrl}/api/auth/logout`, {
      method: 'POST',
      headers: API_CONFIG.headers,
      credentials: 'include' // セッションCookieを送信
    });
    
    // メモリとローカルストレージをクリア
    setUserInMemory(null);
    localStorage.removeItem('user');
  },

  // 現在のユーザー情報取得
  getCurrentUser: async (): Promise<User | null> => {
    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/api/me`, {
        headers: API_CONFIG.headers,
        credentials: 'include' // セッションCookieを送信
      });

      if (!response.ok) {
        if (response.status === 401) {
          // 401エラー時はメモリとlocalStorageをクリア
          setUserInMemory(null);
          localStorage.removeItem('user');
          return null;
        }
        throw new Error(`Failed to get user info: ${response.status}`);
      }

      const user = await response.json();
      setUser(user);
      return user;
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;    }
  }
};